/**
 * @render react
 * @name Newsletter
*/

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';
import { withModal } from 'Components/protons/withModal';
import { withAmp } from 'Components/protons/withAmp';

import { Button } from 'Components/atoms/Button';
import { Image } from 'Components/atoms/Image';
import { Input } from 'Components/atoms/Input';
import { Form } from 'Components/molecules/Form';
import { Heading } from 'Components/molecules/Heading';
import { Label } from 'Components/molecules/Label';
import { Link } from 'Components/molecules/Link';
import { Modal } from 'Components/molecules/Modal';

import { asyncTrackLinkClick } from 'SharedHelpers/tracking';

import renderAmp from './amp';

import './_style.scss';

/**
 * Newsletter component
 * @param {Object} context - extra information coming from Polaris provider
 * @param {string} cta_label - label for the button, e.g. Sign me up
 * @param {Object} extraClassNames - extra classes to be applied to the component
 * @param {boolean} enableTracking - Whether to enable tracking
 * @param {string} inputId - ID for the newsletter input field
 * @param {string} inputName - Name for the newsletter input field
 * @param {string} label - Label for the newsletter input
 * @param {string} labelScreenReaderText - Screen reader text for the newsletter label
 * @param {object} link - props to be passed to Link Component
 * @param {string} linkUrl - URL for the link if showLink is true
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} mainTitle  - main title for the component
 * @param {boolean} showLink - Whether to show a link instead of the input text
 * @param {number} size - supported tag size
 * @param {string} srLabel - aria-label string
 * @param {string} subtitle - sub title for the newsletter
 * @param {string} title - title for the newsletter
 */

class Newsletter extends PureComponent {
  /**
   * Constructor
   */
  constructor(props) {
    super(props);

    this.state = {
      email: '',
    };

    // Bind the methods
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  /**
   * onSubmit event handle. Will pass the entered email value to the parent component
   *
   * @param {SyntheticEvent} e - Event
   */
  async handleSubmit(e) {
    const { onSubmit, toggleModal } = this.props;
    const { value } = this.state;

    if (onSubmit) {
      onSubmit(e, value);
    }
    // function comes from withModal HOC
    toggleModal();
  }

  /**
   * Renders the amp version of the component.
   */
  amp() {
    return renderAmp.bind(this)();
  }

  /**
   * Render our component
   */
  render() {
    const {
      context: { cssPrefix, newsletterURL },
      cta_label,
      extraClassNames,
      enableTracking,
      formUrl,
      image,
      imageSize,
      inputId,
      inputName,
      label,
      labelScreenReaderText,
      link,
      linkUrl,
      loadStyles,
      mainTitle,
      modalOpen, // prop comes from withModal HOC
      placeholder,
      showLink,
      srLabel,
      subtitle,
      title,
      toggleModal, // prop comes from withModal HOC
    } = this.props;
    const { email } = this.state;

    /**
     * Handles the email input change event
     * @param {SyntheticEvent} e - Trigerred event
     */
    const handleInputChange = (e) => {
      // eslint-disable-next-line
      const { target: { value: email } } = e;

      this.setState({
        email,
      });
    };

    return (
      <div
        className={classNames({
          [`${cssPrefix}__newsletter`]: loadStyles,
          ...extraClassNames,
        })}
      >
        <Form
          action={formUrl || newsletterURL}
          onSubmit={this.handleSubmit}
          target="signup-frame"
          method="post"
        >
          {mainTitle
            && (
            <Heading
              size={4}
              extraClassNames={{ [`${cssPrefix}__newsletter--main-heading`]: true }}
            >
              <span>{mainTitle}</span>
            </Heading>
            )}
          <div className={`${cssPrefix}__newsletter--form`}>
            <div
              className={classNames({
                [`${cssPrefix}__newsletter--headings`]: true,
              })}
            >
              <Heading size={3} extraClassNames={{ [`${cssPrefix}__newsletter--title`]: true }}>{title}</Heading>

              {subtitle && <Heading size={4} extraClassNames={{ [`${cssPrefix}__newsletter--subtitle`]: true }}>{subtitle}</Heading>}
            </div>

            <div
              className={classNames({
                [`${cssPrefix}__newsletter--button-group`]: true,
                '-show-link': showLink,
              })}
            >
              {
                showLink
                  ? (
                    <Link
                      extraClassNames={{ [`${cssPrefix}__newsletter--link`]: true, [`${cssPrefix}__button`]: true }}
                      href={linkUrl}
                      title={link.title}
                      screenReaderText={link.screenReaderText || undefined}
                    >
                      {cta_label}
                    </Link>
                  )
                  : (
                    <>
                      <Label
                        content={label}
                        target={inputId || `${cssPrefix}__newsletter--input`}
                        className={`${cssPrefix}__newsletter--label`}
                      >
                        {labelScreenReaderText && (
                          <span className="screen-reader-text">{labelScreenReaderText}</span>
                        )}
                      </Label>
                      <Input
                        id={inputId || `${cssPrefix}__newsletter--input`}
                        type="email"
                        name={inputName}
                        placeholder={placeholder}
                        value={email}
                        onChange={handleInputChange}
                        srLabel={srLabel}
                      />
                      <Button
                        type="submit"
                        srLabel={srLabel}
                        label={cta_label}
                        onClick={async () => {
                          if (enableTracking) {
                            await asyncTrackLinkClick({ event: 'newsletter_block.button_click' });
                          }
                        }}
                      />
                    </>
                  )
              }
            </div>
          </div>
        </Form>

        {image && image.src && (
          <div className={`${cssPrefix}__newsletter--image`}>
            <Image
              size={imageSize}
              {...image}
              displayImageMetadata
            />
          </div>
        )}

        {!showLink && (
          <Modal
            isOpen={modalOpen}
            body={
              <iframe title="signup-frame" name="signup-frame" />
            }
            onClose={toggleModal}
          />
        )}
      </div>
    );
  }
}

/**
 * Valid props
 */
Newsletter.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
    newsletterURL: PropTypes.string,
  }),
  cta_label: PropTypes.string,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  enableTracking: PropTypes.bool,
  formUrl: PropTypes.string,
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    src: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  imageSize: PropTypes.string,
  inputId: PropTypes.string,
  inputName: PropTypes.string,
  label: PropTypes.string,
  labelScreenReaderText: PropTypes.string,
  link: PropTypes.shape(),
  linkUrl: PropTypes.string,
  loadStyles: PropTypes.bool,
  mainTitle: PropTypes.string,
  modalOpen: PropTypes.bool,
  onSubmit: PropTypes.func,
  placeholder: PropTypes.string,
  showLink: PropTypes.bool,
  srLabel: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  toggleModal: PropTypes.func,
};

/**
 * Default props
 */
Newsletter.defaultProps = {
  // eslint-disable-next-line
  cta_label: 'Submit',
  context: {},
  enableTracking: false,
  extraClassNames: {},
  formUrl: '',
  image: null,
  imageSize: 'subscription',
  inputId: null,
  inputName: 'mail',
  label: '',
  labelScreenReaderText: '',
  link: {},
  linkUrl: null,
  loadStyles: true,
  mainTitle: '',
  modalOpen: false,
  onSubmit: null,
  placeholder: '',
  showLink: false,
  srLabel: 'Subscribe',
  subtitle: '',
  title: '',
  toggleModal: () => {},
};

export default compose(withPolaris, withModal, withAmp)(Newsletter);
